import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { FiMinus, FiPlus } from 'react-icons/fi';
import AppSelect from '../../../../shared/components/app-select/AppSelect';
import AppButton from '../../../../shared/components/app-button/AppButton';
import {
  CHILD_AGE_OPTIONS,
  CRUISE_GUEST_LIMITS,
  DEFAULT_CHILD_AGE,
  DEFAULT_INFANT_AGE,
  GUEST_TYPE_HINTS,
  INFANT_AGE_OPTIONS,
  MAX_GUESTS_PER_CABIN,
} from '../../constants/cruiseConstants';
import {
  guestCount,
  guestsSummary,
  plural,
  resizeAges,
} from '../../utils/cruiseUtils';

const ROWS = [
  { key: 'adults', label: 'Adults', hint: GUEST_TYPE_HINTS.adults },
  { key: 'children', label: 'Children', hint: GUEST_TYPE_HINTS.children },
  { key: 'infants', label: 'Infants', hint: GUEST_TYPE_HINTS.infants },
];

/** Guests box with a popover of steppers and child / infant ages. */
function CruiseGuestsField({ guests, onChange }) {
  const [open, setOpen] = useState(false);
  const boxRef = useRef(null);

  useEffect(() => {
    const onDown = (e) => {
      if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, []);

  const step = (key, delta) => {
    const lim = CRUISE_GUEST_LIMITS[key];
    let v = Math.min(lim.max, Math.max(lim.min, guests[key] + delta));
    // One stateroom holds at most MAX_GUESTS_PER_CABIN, infants included.
    if (delta > 0 && guestCount(guests) >= MAX_GUESTS_PER_CABIN) return;
    // One infant per adult.
    if (key === 'infants') v = Math.min(v, guests.adults);
    const next = { ...guests, [key]: v };
    if (key === 'adults') next.infants = Math.min(next.infants, v);
    next.childAges = resizeAges(
      guests.childAges,
      next.children,
      DEFAULT_CHILD_AGE,
    );
    next.infantAges = resizeAges(
      guests.infantAges,
      next.infants,
      DEFAULT_INFANT_AGE,
    );
    onChange(next);
  };

  const setAge = (listKey, index, value) => {
    const list = [...guests[listKey]];
    list[index] = Number(value);
    onChange({ ...guests, [listKey]: list });
  };

  const total = guestCount(guests);
  const hasAges = guests.children > 0 || guests.infants > 0;

  return (
    <div className="cr-field cr-field--guests" ref={boxRef}>
      <button
        type="button"
        className="cr-field__trigger"
        onClick={() => setOpen((o) => !o)}
        aria-expanded={open}
      >
        <span className="cr-field__label">Guests</span>
        <span className="cr-field__value">{plural(total, 'Guest')}</span>
        <span className="cr-field__sub">{guestsSummary(guests)}</span>
      </button>
      {open && (
        <div className="cr-popover">
          {ROWS.map((row) => {
            const cabinFull = guestCount(guests) >= MAX_GUESTS_PER_CABIN;
            const lim = CRUISE_GUEST_LIMITS[row.key];
            const value = guests[row.key];
            const max =
              row.key === 'infants'
                ? Math.min(lim.max, guests.adults)
                : lim.max;
            return (
              <div className="cr-stepper-row" key={row.key}>
                <div>
                  <div className="cr-stepper-row__label">{row.label}</div>
                  <div className="cr-stepper-row__hint">{row.hint}</div>
                </div>
                <div className="cr-stepper">
                  <button
                    type="button"
                    disabled={value <= lim.min}
                    onClick={() => step(row.key, -1)}
                    aria-label={`Remove ${row.label}`}
                  >
                    <FiMinus size={14} />
                  </button>
                  <b>{value}</b>
                  <button
                    type="button"
                    disabled={value >= max || cabinFull}
                    onClick={() => step(row.key, 1)}
                    aria-label={`Add ${row.label}`}
                  >
                    <FiPlus size={14} />
                  </button>
                </div>
              </div>
            );
          })}
          <div className="cr-popover__note">
            Up to {MAX_GUESTS_PER_CABIN} guests per stateroom, infants included.
            One infant per adult.
          </div>
          {hasAges && (
            <div className="cr-ages">
              {guests.childAges.map((age, i) => (
                <AppSelect
                  // eslint-disable-next-line react/no-array-index-key
                  key={`child-${i}`}
                  name={`childAge${i}`}
                  label={`Child ${i + 1} age`}
                  placeholder=""
                  height="40px"
                  isClearable={false}
                  options={CHILD_AGE_OPTIONS}
                  selectedValue={age}
                  onSelect={(opt) => setAge('childAges', i, opt.value)}
                />
              ))}
              {guests.infantAges.map((age, i) => (
                <AppSelect
                  // eslint-disable-next-line react/no-array-index-key
                  key={`infant-${i}`}
                  name={`infantAge${i}`}
                  label={`Infant ${i + 1} age`}
                  placeholder=""
                  height="40px"
                  isClearable={false}
                  options={INFANT_AGE_OPTIONS}
                  selectedValue={age}
                  onSelect={(opt) => setAge('infantAges', i, opt.value)}
                />
              ))}
            </div>
          )}
          <AppButton
            btnText="Done"
            className="cr-btn-block"
            onClick={() => setOpen(false)}
          />
        </div>
      )}
    </div>
  );
}

CruiseGuestsField.propTypes = {
  guests: PropTypes.shape({
    adults: PropTypes.number,
    children: PropTypes.number,
    infants: PropTypes.number,
    childAges: PropTypes.arrayOf(PropTypes.number),
    infantAges: PropTypes.arrayOf(PropTypes.number),
  }).isRequired,
  onChange: PropTypes.func.isRequired,
};

export default CruiseGuestsField;
